import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import CardContent from "@material-ui/core/CardContent";
import CardMedia from "@material-ui/core/CardMedia";
import Typography from "@material-ui/core/Typography";

// Dynamically import all Markdown files and images from InfoDump/pdfs
function importAll(r) {
  return r.keys().map((key) => {
    const module = r(key);
    const src = module.default || module;
    // key looks like './filename.md' -> strip './' and extension
    const base = key.replace(/\.\//, "").replace(/\.[^/.]+$/, "");
    // convert filename to a title: replace -/_ with space and capitalize words
    const title = base
      .replace(/[-_]+/g, " ")
      .split(" ")
      .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");
    return { src, title, base };
  });
}

const markdown = importAll(
  require.context("./InfoDump/articles", false, /\.md$/)
);

const images = importAll(
  require.context("./InfoDump/articles", false, /\.(png|jpe?g|svg)$/)
);

// Dynamically import all text files from InfoDump/articles
const textPaths = require.context("./InfoDump/articles", false, /\.txt$/).keys().map((key) => {
  const module = require("./InfoDump/articles/" + key.replace(/^\.\//, ""));
  const base = key.replace(/\.\//, "").replace(/\.[^/.]+$/, "");
  return { path: module.default || module, base };
});

// Create a map of images by base filename for easy lookup
const imageMap = {};
images.forEach((img) => {
  imageMap[img.base] = img.src;
});

// Create a map of text paths by base filename for easy lookup
const textPathMap = {};
textPaths.forEach((text) => {
  textPathMap[text.base] = text.path;
});

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    background: "transparent",
    minHeight: "100vh",
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(6),
  },
  titleBox: {
    background: "rgba(0, 0, 0, 0.8)",
    padding: theme.spacing(2, 4),
    marginBottom: theme.spacing(4),
    textAlign: "center",
    width: "fit-content",
    margin: "0 auto",
    borderRadius: 20,
  },
  titleText: {
    color: "white",
    textShadow: "0 2px 8px rgba(0, 0, 0, 0.9)",
  },
  cardContainer: {
    maxWidth: 350,
    margin: "1.5rem auto",
    background: "rgba(0, 0, 0, 0.8)",
    color: "white",
  },
  cardDescription: {
    color: "rgba(255, 255, 255, 0.7)",
  },
  media: {
    height: "auto",
    width: "100%",
    objectFit: "contain",
    display: "block",
  },
  cardTitle: {
    textAlign: "center",
  },
}))

const Infodump = () => {
  const classes = useStyles();
  const [textContent, setTextContent] = React.useState({});

  React.useEffect(() => {
    // Fetch all text files
    const loadTexts = async () => {
      const content = {};
      for (const [base, path] of Object.entries(textPathMap)) {
        try {
          const response = await fetch(path);
          content[base] = await response.text();
        } catch (error) {
          console.error(`Failed to load text for ${base}:`, error);
        }
      }
      setTextContent(content);
    };
    loadTexts();
  }, []);

  return (
    <Box component="div" className={classes.mainContainer}>
      <Box className={classes.titleBox}>
        <Typography variant="h4" className={classes.titleText}>
          <span>✨ Infodumps! {"(^>"} ✨</span>
        </Typography>
      </Box>

      <Grid container justifyContent="center">
        {markdown.map((article, i) => (
          <Grid item xs={12} sm={10} md={4} key={i}>
            <a href={`/article/${article.base}`} style={{ textDecoration: "none", color: "inherit" }}>
              <Card className={classes.cardContainer}>
                <CardActionArea>
                  {imageMap[article.base] && (
                    <CardMedia
                      component="img"
                      className={classes.media}
                      image={imageMap[article.base]}
                      title={article.title}
                    />
                  )}
                  {textContent[article.base] && (
                    <CardContent>
                      <Typography variant="body2" className={classes.cardDescription}>
                        {textContent[article.base]}
                      </Typography>
                    </CardContent>
                  )}
                  <CardContent>
                    <Typography variant="h5" className={classes.cardTitle}>
                      {article.title}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </a>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Infodump;
